import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, CalendarClock, CheckCircle, Loader, X } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { useOnClickOutside } from '../../../hooks/useOnClickOutside';
import { staffManagerApiService } from '../../../services/staffManagerApi';
import type { MembershipExpiryResult, StaffTeam } from '../../../types/staffManager';
import { EXPIRY_PRESETS, expiryFromPreset, fromDateInputValue, toDateInputValue } from '../../../utils/staffTenure';
import { cn } from '../../../utils/cn';

interface MembershipExpiryModalProps {
  isOpen: boolean;
  onClose: () => void;
  team: StaffTeam | null;
  member: { user_id: string; username: string; expires_at: number | null } | null;
  onSuccess: (result: MembershipExpiryResult, target: { userId: string; teamId: string }) => void;
}

/** Set, extend or clear a membership's expiry. The join date (tenure) is never touched. */
export const MembershipExpiryModal = ({ isOpen, onClose, team, member, onSuccess }: MembershipExpiryModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [preset, setPreset] = useState<string>('none');
  const [customDate, setCustomDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<MembershipExpiryResult | null>(null);
  useOnClickOutside(modalRef, onClose);

  useEffect(() => {
    if (isOpen) {
      if (member?.expires_at) {
        setPreset('custom');
        setCustomDate(toDateInputValue(member.expires_at));
      } else {
        setPreset('none');
        setCustomDate('');
      }
      setError(null);
      setSuccess(null);
      setSubmitting(false);
    }
  }, [isOpen, member?.expires_at]);

  const expiresAt = useMemo(() => {
    if (preset === 'custom') return fromDateInputValue(customDate);
    const p = EXPIRY_PRESETS.find((x) => x.id === preset);
    return expiryFromPreset(p?.days ?? null);
  }, [preset, customDate]);
  const invalid = preset === 'custom' && (expiresAt === null || expiresAt <= Date.now());
  const unchanged = member ? (member.expires_at ?? null) === expiresAt : true;

  if (!isOpen || !team || !member) return null;

  const submit = async () => {
    if (invalid) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await staffManagerApiService.setMembershipExpiry({ user_id: member.user_id, team_id: team.id, expires_at: expiresAt });
      setSuccess(result);
      onSuccess(result, { userId: member.user_id, teamId: team.id });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update expiry');
    } finally {
      setSubmitting(false);
    }
  };

  const content = (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[9999] p-4">
      <div ref={modalRef} className="glass-card p-4 md:p-6 rounded-lg w-full max-w-lg max-md:max-w-[95vw] relative">
        <button type="button" onClick={onClose} className="absolute top-3 right-3 text-glass-400 hover:text-white touch-manipulation" aria-label="Close">
          <X />
        </button>
        <div className="flex items-center gap-3 mb-4">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-accent-500/15 text-accent-300">
            <CalendarClock className="w-4 h-4" />
          </span>
          <div>
            <h2 className="text-xl font-bold text-white leading-tight">Membership expiry</h2>
            <p className="text-xs text-glass-400">
              @{member.username} in {team.display_name} · currently{' '}
              {member.expires_at ? `expires ${format(new Date(member.expires_at), 'd MMM yyyy')}` : 'permanent'}
            </p>
          </div>
        </div>

        {success ? (
          <div className="space-y-4">
            <div className="rounded-xl border border-green-500/30 bg-green-500/10 p-4 flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-300 flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <div className="text-sm font-semibold text-green-200">{success.message}</div>
                <p className="text-xs text-green-100/80 mt-1">
                  {success.expires_at ? `Now expires ${format(new Date(success.expires_at), 'd MMM yyyy HH:mm')}.` : 'Membership is now permanent.'}
                  {success.renewed_groups.length > 0 && (
                    <>
                      {' '}
                      Groups renewed to match: <span className="font-mono">{success.renewed_groups.join(', ')}</span>
                    </>
                  )}
                </p>
              </div>
            </div>
            <div className="flex justify-end">
              <button type="button" onClick={onClose} className="glass-button">
                Done
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              {EXPIRY_PRESETS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPreset(p.id)}
                  aria-pressed={preset === p.id}
                  className={cn(
                    'h-8 px-3 rounded-md text-xs font-semibold border transition-colors touch-manipulation',
                    preset === p.id ? 'bg-accent-500/20 text-accent-200 border-accent-400/40' : 'border-glass-200/20 text-glass-400 hover:text-white',
                  )}
                >
                  {p.id === 'none' ? 'Make permanent' : `+${p.label}`}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setPreset('custom')}
                aria-pressed={preset === 'custom'}
                className={cn(
                  'h-8 px-3 rounded-md text-xs font-semibold border transition-colors touch-manipulation',
                  preset === 'custom' ? 'bg-accent-500/20 text-accent-200 border-accent-400/40' : 'border-glass-200/20 text-glass-400 hover:text-white',
                )}
              >
                Pick a date
              </button>
              {preset === 'custom' && (
                <input
                  type="date"
                  className="glass-input h-8 py-0 text-sm"
                  value={customDate}
                  min={toDateInputValue(Date.now() + 86_400_000)}
                  onChange={(e) => setCustomDate(e.target.value)}
                  aria-label="Expiry date"
                />
              )}
            </div>
            {invalid ? (
              <p className="text-xs text-red-300">Pick a date in the future.</p>
            ) : (
              <p className="text-[11px] text-glass-500">
                {expiresAt ? `Expires ${formatDistanceToNow(new Date(expiresAt), { addSuffix: true })} (${format(new Date(expiresAt), 'd MMM yyyy')}).` : 'No expiry.'} The join date is not
                affected; the team's auto-assigned groups follow the new expiry.
              </p>
            )}

            {error && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-200 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> <span>{error}</span>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button type="button" onClick={onClose} className="glass-button-secondary" disabled={submitting}>
                Cancel
              </button>
              <button type="button" onClick={() => void submit()} className="glass-button min-w-[8rem]" disabled={submitting || invalid || unchanged}>
                {submitting ? <Loader className="w-4 h-4 animate-spin" /> : 'Save expiry'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(content, document.body);
};
